import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../components/icons/Icon";

/**
 * Displaced-session modal. Terminal surface that informs the
 * caller their account session was revoked by a concurrent
 * login (or an explicit logout from another device).
 *
 * Surface design lock-in mirrors the global poker-room standard
 * (PokerStars / GGPoker / partypoker / 888):
 *
 *   - Blue informational tint + 🔄 icon — the displacement is
 *     not an error; the player just needs to sign in again.
 *   - Body copy switches on `seated` so a caller mid-hand gets
 *     a reassurance cue that their chips are kept safe on the
 *     server side; out-of-hand callers see the bare summary.
 *   - 10-second numeric countdown (PokerStars convention — also
 *     the partypoker / 888 default; GGPoker uses 7s). The last
 *     two seconds switch to an amber pulse so the dwindling
 *     window reads as urgent without changing severity colour.
 *   - "다시 로그인" CTA fires immediately so a player who
 *     already understood the message can skip the countdown
 *     (PokerStars + GGPoker "Sign in again" parity).
 *
 * SESSION-REVOKE ONLY. The account session was invalidated (new
 * login / explicit logout); the auth-lane teardown
 * (`runLogoutSequence`) already fired the moment the revoke landed.
 * This component drives the dwell countdown + CTA only; `onDismiss`
 * clears the `pendingRevokeNotice` latch (the modal's mount gate), an
 * INDEPENDENT one-way latch the teardown deliberately does not touch
 * — so the modal dwells instead of flashing for one render. The
 * surface-displaced ("active in another tab", non-logout) case is a
 * different fact handled by `SurfaceDisplacedModal`.
 * The single `onDismiss` callback fires exactly once (a
 * second invocation is guarded so a click during the final
 * setInterval tick doesn't fan out).
 */

export interface DisplacedModalProps {
  /** True when the caller was seated at a table when the
   *  revoke landed. Drives the reassurance copy variant; out-
   *  of-hand callers get the bare summary. */
  readonly seated: boolean;
  /** Single-fire dismiss callback. The App-root wiring routes this
   *  to `clearPendingNotices`, which clears the `pendingRevokeNotice`
   *  latch so the modal unmounts. The auth-lane teardown already ran
   *  upstream; this only governs the modal dwell. */
  readonly onDismiss: () => void;
  /** Countdown duration in seconds. Defaults to 10 (the
   *  PokerStars / partypoker / 888 standard); tests inject
   *  `0` for the immediate-fire branch or a finite tick count
   *  for deterministic timer assertions. */
  readonly countdownSeconds?: number;
}

const DEFAULT_COUNTDOWN_SECONDS = 10;
/** Last two seconds switch to amber pulse — urgency cue. */
const AMBER_PULSE_THRESHOLD = 2;

export function DisplacedModal(props: DisplacedModalProps): JSX.Element | null {
  const { t } = useTranslation();
  const total = props.countdownSeconds ?? DEFAULT_COUNTDOWN_SECONDS;
  const [secondsLeft, setSecondsLeft] = useState<number>(total);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const firedRef = useRef<boolean>(false);

  const fireDismiss = useMemo(
    () => () => {
      if (firedRef.current) return;
      firedRef.current = true;
      props.onDismiss();
    },
    [props],
  );

  useEffect(() => {
    if (total <= 0) {
      fireDismiss();
      return;
    }
    const interval = window.setInterval(() => {
      setSecondsLeft((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          window.clearInterval(interval);
          fireDismiss();
          return 0;
        }
        return next;
      });
    }, 1_000);
    return () => window.clearInterval(interval);
  }, [total, fireDismiss]);

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  if (typeof document === "undefined") return null;

  const pulse = secondsLeft <= AMBER_PULSE_THRESHOLD;
  const bodyKey = props.seated
    ? "app.displaced_body_seated"
    : "app.displaced_body";

  return createPortal(
    <div
      className="system-modal displaced-modal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="displaced-modal-title"
      aria-describedby="displaced-modal-body"
      data-pulse={pulse ? "true" : "false"}
    >
      <div className="system-modal__card">
        <div className="system-modal__icon" aria-hidden="true">
          <Icon name="refresh" size={28} />
        </div>
        <h2 id="displaced-modal-title" className="system-modal__title">
          {t("app.displaced_title")}
        </h2>
        <p
          id="displaced-modal-body"
          className="system-modal__body"
          data-testid="displaced-modal-body"
        >
          {t(bodyKey)}
        </p>
        <p
          className="system-modal__note"
          aria-live="polite"
          data-pulse={pulse ? "true" : "false"}
          data-testid="displaced-modal-countdown"
        >
          {t("app.displaced_countdown", { seconds: secondsLeft })}
        </p>
        <button
          ref={buttonRef}
          type="button"
          className="btn system-modal__cta"
          onClick={fireDismiss}
          data-testid="displaced-modal-cta"
        >
          {t("app.displaced_cta")}
        </button>
      </div>
    </div>,
    document.body,
  );
}
